import * as React from "react";
import { useEffect, useState } from "react";
import { useAuth0 } from "../../context/react-auth0-spa";
import StationsListing from "../../components/stationsListingV3";
import Map from "../../components/mapVersion2";
import GriddedLocationSelection from "../../components/griddedLocationSelection";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Header from "./grapeRipeRotHeader";
import SubHeader from "./grapeRipeRotSubHeader";
import Footer from "./grapeRipeRotFooter";

import useStations from "../../utils/hooks/useStations";
import useGridPoints from "../../utils/hooks/useGridPoints";
import { formatDate } from "../../utils/helpers";

// @ts-ignore
import ModelMetadata from "../../components/models/grapeRipeRot/grapeRipeRotMetadata.json";
import "../../components/models/grapeRipeRot/grapeRipeRotStylesheet.css";

import { useTracking } from "../../utils/hooks/useTracking";
import {
  GrapeRipeRotApiResponse,
  GrapeRipeRotPredictionInput,
} from "../../components/models/grapeRipeRot/grapeRipeRotTypes";
import { PageLoadingProvider, usePageLoading } from "../../components/Status/PageLoadingProvider";
import { ResultsTable } from './_resultsTable';
import References from './_references';
import ManagementGuide from './_managementGuide';

const GRAPE_RIPE_ROT_URL = `${process.env.GATSBY_NEWA_API_ORIGIN}/v1/grape-ripe-rot/calculate`;

const h1Text = "Grape Ripe Rot Risk Assessment";

///////////////////////////////////////////////////////////////////////////////////////////////////
const GrapeRipeRotContent: React.FC = () => {
  const { isAuthenticated, user } = useAuth0();
  const { setLoading, setError } = usePageLoading();

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // LOCATION CONFIGURATION
  /////////////////////////////////////////////////////////////////////////////////////////////////
  const [selectedDataSource, setSelectedDataSource] = useState("NEWA");
  const { gridPoint, setGridPoint, gridPointGeoJSON, favoriteGridPoints } =
    useGridPoints();
  const { stationList, station, setStation, favoriteStations, geoJSON } =
    useStations();
  const sid = station ? station.id : "gen newa";

  const handleStationSelect = (selectedStation) => {
    setStation(selectedStation);
  };

  const handleDataSourceChange = (dataSource) => {
    setSelectedDataSource(dataSource);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // MODEL CONFIGURATION
  /////////////////////////////////////////////////////////////////////////////////////////////////
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPhenologicalStage, setSelectedPhenologicalStage] = useState<string | null>(null);
  const [riskData, setRiskData] = useState<GrapeRipeRotApiResponse | null>(null);
  const [isLoadingRiskData, setIsLoadingRiskData] = useState(false);

  function getDefaultStartDate(currentDate: Date): Date {
    const currentYear = currentDate.getFullYear();
    // If before May, use previous year's May 1
    if (currentDate.getMonth() < 4) {
      return new Date(currentYear - 1, 4, 1);
    }
    // Otherwise, May 1 of current year
    return new Date(currentYear, 4, 1);
  }

  const startDate = getDefaultStartDate(currentDate);

  useTracking({ modelId: ModelMetadata.title, stationId: station?.id });

  const handleSelectPhenologicalStage = (stageId: string) => {
    setSelectedPhenologicalStage(stageId);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // API CALL
  /////////////////////////////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    const calculate = async () => {
      if (!selectedPhenologicalStage) {
        setRiskData(null);
        return;
      }

      const latitude = selectedDataSource === "NEWA"
        ? station?.lat
        : gridPoint?.latitude;
      const longitude = selectedDataSource === "NEWA"
        ? station?.lon
        : gridPoint?.longitude;

      if (!latitude || !longitude) {
        console.log("No location available for selected data source");
        setRiskData(null);
        return;
      }

      const stationId = selectedDataSource === "NEWA" ? station?.id : undefined;

      try {
        setError(null);
        setLoading(true, "Calculating ripe rot risk...");
        const results = await getRipeRotRiskData({
          phenologicalStage: selectedPhenologicalStage,
          latitude,
          longitude,
          stationId,
          startDate: formatDate(startDate),
          endDate: formatDate(currentDate),
        });
        setLoading(false);
        setRiskData(results);
      } catch (error) {
        console.log("Ripe rot API error:", error);
        setLoading(false);
        setRiskData(null);
        setError("Unable to reach the ripe rot risk service. Please try again shortly.");
      }
    };
    calculate();
  }, [
    currentDate,
    selectedPhenologicalStage,
    selectedDataSource,
    station,
    gridPoint,
  ]);

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // CSV DOWNLOAD
  /////////////////////////////////////////////////////////////////////////////////////////////////
  const handleDownloadCSV = () => {
    if (!riskData?.hourlyData?.length) return;
    const headers = [
      "datetime",
      "isForecast",
      "temperatureC",
      "leafWetnessMinutes",
      "relativeHumidity",
      "wetnessEventStatus",
      "hourlyGrapeRipeRotEnvironmentalRisk",
      "hourlyGrapeRipeRotRisk",
    ];
    const rows = riskData.hourlyData ? riskData.hourlyData.map((row) =>
      headers.map((h) => {
        const val = row[h];
        if (h === "datetime") return `"${val}"`;
        return val ?? "";
      }).join(",")
    ) : [];
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    const locationName = selectedDataSource === "NEWA"
      ? station?.name || "station"
      : "gridpoint";
    a.download = `${locationName}-ripe-rot-${selectedPhenologicalStage}.csv`;
    a.href = window.URL.createObjectURL(blob);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // COLLAPSIBLE CARDS
  /////////////////////////////////////////////////////////////////////////////////////////////////
  const [expandedCards, setExpandedCards] = useState({
    dataSourceDetails: true,
    modelDetails: true,
    dailyTable: true,
    hourlyTable: false,
  });

  const toggleExpand = (card) => {
    setExpandedCards((prevState) => ({
      ...prevState,
      [card]: !prevState[card],
    }));
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // MISCELLANEOUS
  /////////////////////////////////////////////////////////////////////////////////////////////////
  const mapCustomHeight = "275px";

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // RENDER OUTPUT
  /////////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <>
      <Header location={undefined} color={undefined}></Header>
      <SubHeader h1Text={h1Text} station={station}></SubHeader>
      <div>
        <main>
          <div id="app-container" className="grape-ripe-rot-container">
            {/* LOCATION DETAILS */}
            <div id="data-source-details" className="card-small">
              <h1 onClick={() => toggleExpand("dataSourceDetails")}>
                <span>Location details</span>
                <span>{expandedCards.dataSourceDetails ? "▼" : "►"}</span>
              </h1>
              {expandedCards.dataSourceDetails && (
                <div>
                  <div className="button-container">
                    <br/>
                    <label>Choose a Data Source</label>
                    <div className="toggle-button">
                      <button
                        id="sourceNewa"
                        className={`toggle-button-option ${
                          selectedDataSource === "NEWA" ? "active" : ""
                        }`}
                        onClick={() => handleDataSourceChange("NEWA")}
                      >
                        NEWA Station
                      </button>
                      <button
                        id="sourceGrid"
                        className={`toggle-button-option ${
                          selectedDataSource === "GRID" ? "active" : ""
                        }`}
                        onClick={() => handleDataSourceChange("GRID")}
                      >
                        Grid Data
                      </button>
                    </div>
                  </div>
                  <br/>
                  <div>
                    {selectedDataSource === "NEWA" ? (
                      <div>
                        <StationsListing
                          options={stationList}
                          value={station}
                          setValue={handleStationSelect}
                          label={"Stations"}
                        ></StationsListing>
                        <br/>
                        <div>
                          <Map
                            station={station}
                            setStation={handleStationSelect}
                            geoJSON={geoJSON}
                            customHeight={mapCustomHeight}
                          ></Map>
                        </div>
                        <div>
                          {station === null || station === undefined ? (
                            <h2>Select a station...</h2>
                          ) : (
                            <>
                              <h2>
                                {station.name}, {station.state}
                              </h2>
                              <p>
                                Courtesy of{" "}
                                <a
                                  href="{station.affiliationUrl}"
                                  target="blank"
                                >
                                  {station.affiliation}
                                </a>
                                <br/>
                                In service since {station.startYear}
                                <br/>
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <GriddedLocationSelection
                        gridPoint={gridPoint}
                        setGridPoint={setGridPoint}
                        geoJSON={gridPointGeoJSON}
                        favoriteGridPoints={favoriteGridPoints}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* SELECT GROWTH STAGE (model details + results merged) */}
            <div id="model-details" className="card-small">
              <h1 onClick={() => toggleExpand("modelDetails")}>
                <span>Model Details</span>
                <span>{expandedCards.modelDetails ? "▼" : "►"}</span>
              </h1>
              {expandedCards.modelDetails && (
                <div className="relative">
                  <div className="input-container">
                    <br/>
                    <label>Select a grape growth stage</label>
                    <div className="stage-radio-group">
                      {ModelMetadata.phenologicalStages.map((stage, index) => (
                        <label key={index} className="stage-radio">
                          <input
                            type="radio"
                            name="phenologicalStage"
                            value={stage.stageId}
                            checked={selectedPhenologicalStage === stage.stageId}
                            onChange={() => handleSelectPhenologicalStage(stage.stageId)}
                          />
                          <span>{stage.stageName} ({stage.riskLabel})</span>
                        </label>
                      ))}
                    </div>
                    <div className="stage-description">
                      Grapes become more susceptible to ripe rot infection as berries
                      mature, with greatest risk from veraison to harvest.
                    </div>
                    {!selectedPhenologicalStage && (
                      <div>
                        <p>
                          <em>
                            Select a grape growth stage to see the risk assessment value
                          </em>
                        </p>
                      </div>
                    )}
                    <br/>
                    <label htmlFor="datePicker">Choose the date</label>
                    <DatePicker
                      id="datePicker"
                      selected={currentDate}
                      onChange={(date) => date && setCurrentDate(date)}
                      maxDate={new Date()}
                    ></DatePicker>
                    <br/>
                  </div>

                  <hr className="results-divider"/>

                  <div style={{ padding: "10px" }}>
                    {!riskData ? (
                      <div>
                        <em>
                          {!selectedPhenologicalStage
                            ? "Select a location and grape growth stage to see the risk assessment value"
                            : isLoadingRiskData
                              ? "Loading..."
                              : "No risk data available. Please check your location and try again later."}
                        </em>
                      </div>
                    ) : (
                      <div style={{ textAlign: "center" }}>
                        {(() => {
                          const selectedStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
                          const selectedDay =
                            riskData.dailyData?.find((d) => d.date === selectedStr) ??
                            riskData.dailyData?.at(-1);
                          if (!selectedDay) {
                            return <p><em>No daily data returned.</em></p>;
                          }
                          return (
                            <>
                              <div className={`risk-indicator ${selectedDay.isHighRisk ? "risk-yes" : "risk-no"}`}>
                                {selectedDay.isHighRisk ? "Risk: High" : "Risk: Low"}
                              </div>
                              <p style={{ marginTop: "10px", fontSize: "14px" }}>
                                {selectedDay.date
                                  ? new Date(selectedDay.date + "T00:00:00").toLocaleDateString()
                                  : ""}
                              </p>
                            </>
                          );
                        })()}
                        <p style={{ marginTop: "10px", fontSize: "12px", color: "#888" }}>
                          Model version {riskData.metadata?.modelVersion}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>

          <ResultsTable
            riskData={riskData}
            hourlyExpanded={expandedCards.hourlyTable}
            onToggleHourly={() => toggleExpand("hourlyTable")}
            onDownloadCSV={handleDownloadCSV}
          />

          {/* MANAGEMENT GUIDE */}
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="mb-16 sm:mb-18 md:mb-20">
              <h2 className="mb-3 font-semibold text-gray-600 sm:mb-5 md:mb-6 md:text-2xl">
                Management Guide
              </h2>
              <ManagementGuide />
            </div>

            {/* REFERENCES */}
            <div className="mb-16 sm:mb-18 md:mb-20">
              <h2 className="mb-3 font-semibold text-gray-600 sm:mb-5 md:mb-6 md:text-2xl">
                References
              </h2>
              <div className="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
                <div className="px-6 py-4 bg-white">
                  <References />
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer></Footer>
      </div>
    </>
  );
};

// Main wrapper component with loading provider
const GrapeRipeRot: React.FC = () => {
  return (
    <PageLoadingProvider>
      <GrapeRipeRotContent/>
    </PageLoadingProvider>
  );
};

///////////////////////////////////////////////////////////////////////////////////////////////////
// API FUNCTION
///////////////////////////////////////////////////////////////////////////////////////////////////
const getRipeRotRiskData = async ({
                                    phenologicalStage,
                                    latitude,
                                    longitude,
                                    stationId,
                                    startDate,
                                    endDate,
                                  }: {
  phenologicalStage: string;
  latitude: number;
  longitude: number;
  stationId?: string;
  startDate: string;
  endDate: string;
}): Promise<GrapeRipeRotApiResponse | null> => {
  if (!process.env.GATSBY_NEWA_API_ORIGIN) {
    console.warn("GATSBY_NEWA_API_ORIGIN is not configured");
    return null;
  }

  const body: GrapeRipeRotPredictionInput = {
    phenologicalStage,
    latitude,
    longitude,
    stationId,
    startDate,
    endDate,
  };

  const response = await fetch(GRAPE_RIPE_ROT_URL, {
    method: "POST",
    headers: {
      'Content-Type': "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Ripe rot API returned ${response.status}`);
  }

  const data: GrapeRipeRotApiResponse = await response.json();
  return data;
};

export default GrapeRipeRot;
